import _ from 'underscore';

/**
 * @typedef {Object} Coordinate
 * @property {number} x - x-axis value
 * @property {number} y - y-axis value
 */

/**
 * @typedef {Coordinate[]} CoordinateTuple [0]=corner coordinates, [1]=opposite corner coordinates
 */

/**
  * Given a list of rectangles, each represented as a tuple of coordinates of its diagonally-
  * opposite corners, return the index within the list that represents the closest rectangle to
  * the target. Assumes no overlap.
  *
  * @param {Coordinate} target - coordinates for comparison point
  * @param {CoordinateTuple[]} elementCoordinates - list of coordinate tuples indicating bounds of
  * rectangle
  * @returns {number} - index within elementCoordinates list
  */
export const whichIsClosest = (target, elementCoordinates) => {
  const { x, y } = target;
  let minDistance;
  let minIndex = 0;

  for (let i = 0; i < elementCoordinates.length; i += 1) {
    const [topLeft, bottomRight] = elementCoordinates[i];
    let distance;

    if (
      (x >= topLeft.x && x <= bottomRight.x) &&
      (y >= topLeft.y && y <= bottomRight.y)
    ) { // target is inside element, return found immediately
      return i;
    }

    if (x >= topLeft.x && x <= bottomRight.x) {
      distance = Math.min(Math.abs(y - topLeft.y), Math.abs(y - bottomRight.y));
    } else if (y >= topLeft.y && y <= bottomRight.y) {
      distance = Math.min(Math.abs(x - topLeft.x), Math.abs(x - bottomRight.x));
    } else {
      const dx = Math.min(Math.abs(x - topLeft.x), Math.abs(x - bottomRight.x)) ** 2;
      const dy = Math.min(Math.abs(y - topLeft.y), Math.abs(y - bottomRight.y)) ** 2;

      distance = Math.sqrt(dx + dy);
    }

    if (_.isUndefined(minDistance) || distance < minDistance) {
      minIndex = i;
      minDistance = distance;
    }
  }

  return minIndex;
};
